import type { HTTP_METHOD } from "next/dist/server/web/http";
import type { IStringifyOptions } from "qs";
import qs from "qs";

import {
  createResponseError,
  isResponseErrorException,
  unexpectedError,
  unparseableResponseError,
} from "@/services/http/helpers/error";
import { isFullUrl } from "@/services/http/helpers/is-full-url";

import { getBaseUrl } from "./helpers";

export type HttpClientInit = Omit<RequestInit, "body"> & {
  body?: unknown;
  params?: Record<string, unknown>;
  queryOptions?: IStringifyOptions;
};

// const REVALIDATE_TIME = 60 * 3;

/**
 * Constructs a full URL by combining a base URL with optional query parameters,
 * applying the provided query options for serialization. The base URL is determined
 * by checking if the provided URL is a full URL; if not, it prepends the environment's
 * base API URL. This function is useful for preparing request URLs with dynamic query
 * parameters.
 *
 * @param url - The endpoint URL. If this is not a full URL, the environment's base API URL
 *              is prepended.
 * @param params - An optional record of query parameters to be included in the URL. Each
 *                 key-value pair in the record is converted into a query string parameter.
 * @param queryOptions - Optional `qs` library options for serializing the query parameters.
 *                       These options allow for customization of the serialization process,
 *                       such as how arrays and objects are encoded.
 * @returns The fully resolved URL as a string, including any query parameters if provided.
 *
 * @example
 * // With environment base API URL as "https://api.example.com" and without queryOptions
 * // Returns "https://api.example.com/endpoint"
 * const fullUrl = resolveURL('/endpoint');
 *
 * @example
 * // Returns "https://api.example.com/endpoint?param1=value1&param2=value2"
 * const fullUrl = resolveURL('/endpoint', { param1: 'value1', param2: 'value2' }, { encode: false });
 */
const resolveURL = (url: string, params: HttpClientInit["params"], queryOptions: HttpClientInit["queryOptions"]) => {
  const baseUrl = isFullUrl(url) ? url : `${getBaseUrl()}${url}`;
  const fullUrl = new URL(baseUrl);

  if (params) {
    const queryParams = qs.stringify(params, queryOptions);
    for (const [key, value] of new URLSearchParams(queryParams).entries()) {
      fullUrl.searchParams.append(key, value);
    }
  }

  return fullUrl.toString();
};

const request =
  (method: HTTP_METHOD) =>
  /**
   * Executes a network request using the Fetch API with specified HTTP method and options.
   * This function is designed to handle both successful and error responses, throwing
   * custom errors as needed. It also integrates query parameters and body data into the
   * request as specified by the caller.
   *
   *
   * @param url - The endpoint URL. This can be a full URL or a path, in which case the environment's
   *                    base API URL is prepended.
   * @param  options - The options for the fetch request. This includes any HTTP headers,
   *                                   body data, query parameters, and other request options. The `body`
   *                                   field is stringified JSON, and `params` are appended to the URL as
   *                                   query parameters.
   * @returns - A promise that resolves with the response data of type `ResponseData`
   *                                    if the request is successful.
   * @throws  - Throws an error of type `ResponseErrorData` if the response is an error response.
   *                               This includes network errors, HTTP status code errors
   * @throws  - Throws an `Unresolved JSON error` when the response cannot be parsed as JSON.
   * @throws  - Throws a generic Error with an error message if an unexpected error occurs.
   *
   * @example
   * // Making a GET request to fetch user dataZ
   * const userData = await httpClient.get('/users/1');
   *
   * @example
   * // Making a POST request to create a new user
   * const newUser = await httpClient.post('/users', { body: { name: 'Jane Doe' } });
   */
  async (url: string, options: HttpClientInit = {}): Promise<unknown> => {
    const { body, headers, params, queryOptions, ...requestOptions } = options;
    const init: RequestInit = {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(headers as Record<string, string>),
      },
      ...requestOptions,
    };

    if (body !== undefined && body !== null) {
      init.body = JSON.stringify(body);
    }

    // if (!requestOptions.cache || !requestOptions.next) {
    //   init.next = {
    //     revalidate: REVALIDATE_TIME,
    //   };
    // }

    try {
      const targetUrl = resolveURL(url, params, queryOptions);
      const response = await fetch(targetUrl, init);
      let result: unknown;

      try {
        result = await response.json();
      } catch (ex) {
        console.error({ response });
        throw unparseableResponseError(targetUrl, ex);
      }

      if (!response.ok) {
        console.error(response);

        // eslint-disable-next-line @typescript-eslint/only-throw-error -- Error is thrown
        throw createResponseError(result);
      }

      return result;
    } catch (ex) {
      if (isResponseErrorException(ex)) {
        throw ex.error;
      }

      // TODO Remove console.error and use project report service

      console.error(ex);
      throw unexpectedError(ex);
    }
  };

/**
 * An HTTP client object for making network requests. This client provides functions
 * for making HTTP requests using common HTTP methods: GET, POST, PATCH, PUT, and DELETE.
 * Each method is pre-configured to use the appropriate HTTP verb. The client abstracts
 * the complexity of configuring fetch requests, including setting headers, serializing
 * body content, and handling query parameters. It is designed to work seamlessly with
 * JSON APIs but can be customized for other content types via the headers in `HttpClientInit`.
 *
 * The functions return a promise with the response data, automatically parsed from JSON,
 * or throw an error if the request fails due to network issues, server errors, or if the
 * response cannot be parsed as JSON. Errors from the server are thrown as custom errors
 * with detailed information about the request failure.
 *
 * @example
 * // Using httpClient to GET data
 * httpClient.get('/user/1')
 *   .then(data => console.log(data))
 *   .catch(error => console.error(error));
 *
 * @example
 * // Using httpClient to POST data
 * httpClient.post('/user', { body: { name: 'Jane Doe' } })
 *   .then(data => console.log(data))
 *   .catch(error => console.error(error));
 */
export const httpClient = {
  get: request("GET"),
  post: request("POST"),
  patch: request("PATCH"),
  put: request("PUT"),
  delete: request("DELETE"),
};
